import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import * as Sentry from '@sentry/react'
import type { Middleware } from 'redux'
import type { LoggerProvider } from './types'
import type { LoggerTrackProperties, LoggerUser } from '../types'
import { sanitizeObject } from '../sensitiveFields'
import { isSandbox } from 'src/utils/env'

function toLogRocketExtra(
  data: Record<string, unknown> | undefined,
): Record<string, string> | undefined {
  if (!data) {
    return undefined
  }

  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(data)) {
    result[key] = typeof value === 'string' ? value : JSON.stringify(value)
  }

  return result
}

const EXCLUDED_NETWORK_PATTERNS = [
  '/auth/refresh',
  '/dashboards/updates/',
  '/updates/failed_payments',
  '/integrations/lms/state',
  '/integrations/background-check/report',
  'intercom.io',
  'sentry.io',
]

/** Partner IDs (UUIDs) whose sessions are never recorded. Env: REACT_APP_LOGROCKET_BLOCKED_PARTNER_IDS (comma-separated). */
const LOGROCKET_BLOCKED_PARTNER_IDS: string[] = [
  '9737e167-2032-4d65-8ede-52a039860cb4',
  ...(process.env.REACT_APP_LOGROCKET_BLOCKED_PARTNER_IDS?.split(',')
    .map((id) => id.trim())
    .filter(Boolean) ?? []),
]

const DEFAULT_RATE_CLIENT = 1
const DEFAULT_RATE_CONTRACTOR = 1
const DEFAULT_RATE_AUTH = 1
const DEFAULT_RATE_ONBOARDING = 1
const DEFAULT_RATE_OTHER = 0.2

/** Partners whose sessions are never recorded (e.g. high-volume, iframe-heavy). */
const EXCLUDED_PARTNERS = [
  'airspace',
  ...(process.env.REACT_APP_LOGROCKET_EXCLUDED_PARTNERS?.split(',')
    .map((p) => p.trim())
    .filter(Boolean) ?? []),
]

function isExcludedPartnerPath(pathname: string): boolean {
  return EXCLUDED_PARTNERS.some(
    (partner) => pathname.includes(`/${partner}/`) || pathname.endsWith(`/${partner}`),
  )
}

function parseRate(envValue: string | undefined, defaultRate: number): number {
  if (envValue == null || envValue === '') {
    return defaultRate
  }
  const n = parseFloat(envValue)

  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : defaultRate
}

type Segment = 'client' | 'contractor' | 'auth' | 'onboarding' | 'other'

function getSegmentForPath(pathname: string): Segment {
  if (
    pathname.includes('/sign-in') ||
    pathname.includes('/sign-up') ||
    pathname.includes('/enter-code') ||
    pathname.includes('/pre-onboarding')
  ) {
    return 'auth'
  }

  if (pathname.includes('/onboarding/')) {
    return 'onboarding'
  }

  if (pathname.includes('/client/')) {
    return 'client'
  }

  if (pathname.includes('/contractor/')) {
    return 'contractor'
  }

  return 'other'
}

function getSamplingRateForSegment(segment: Segment): number {
  switch (segment) {
    case 'client':
      return parseRate(process.env.REACT_APP_LOGROCKET_RATE_CLIENT, DEFAULT_RATE_CLIENT)
    case 'contractor':
      return parseRate(process.env.REACT_APP_LOGROCKET_RATE_CONTRACTOR, DEFAULT_RATE_CONTRACTOR)
    case 'auth':
      return parseRate(process.env.REACT_APP_LOGROCKET_RATE_AUTH, DEFAULT_RATE_AUTH)
    case 'onboarding':
      return parseRate(process.env.REACT_APP_LOGROCKET_RATE_ONBOARDING, DEFAULT_RATE_ONBOARDING)
    case 'other':
    default:
      return parseRate(process.env.REACT_APP_LOGROCKET_RATE_OTHER, DEFAULT_RATE_OTHER)
  }
}

export class LogRocketProvider implements LoggerProvider {
  private initialized = false
  private logrocketInitialized = false
  private sessionURL: string | null = null
  private sessionURLCallbacks: Array<(url: string) => void> = []

  init(): void {
    const appId = process.env.REACT_APP_LOGROCKET_APP_ID

    if (this.initialized || !appId) {
      return
    }

    const skipLogRocket =
      isSandbox() || process.env.REACT_APP_LOGROCKET_RECORDING_DISABLED === 'true'

    if (skipLogRocket) {
      this.initialized = true

      return
    }

    let shouldSendSession: boolean | null = null

    LogRocket.init(appId, {
      release: process.env.REACT_APP_VERSION,
      shouldSendData: () => {
        if (
          typeof window !== 'undefined' &&
          (window as Window & { __lrBlockSession?: boolean }).__lrBlockSession
        ) {
          return false
        }

        if (shouldSendSession === null) {
          const pathname = typeof window !== 'undefined' ? window.location.pathname : ''

          if (isExcludedPartnerPath(pathname)) {
            shouldSendSession = false
          } else {
            const segment = getSegmentForPath(pathname)
            const rate = getSamplingRateForSegment(segment)
            shouldSendSession = Math.random() < rate
          }
        }

        return shouldSendSession
      },
      console: {
        shouldAggregateConsoleErrors: true,
        isEnabled: {
          log: true,
          debug: false,
          info: true,
          warn: false,
          error: false,
        },
      },
      network: {
        requestSanitizer: (request) => {
          if (EXCLUDED_NETWORK_PATTERNS.some((pattern) => request.url.includes(pattern))) {
            return null
          }

          if (request.headers?.Authorization) {
            request.headers.Authorization = '[REDACTED]'
          }

          return request
        },
        responseSanitizer: (response) => {
          if (response.body) {
            try {
              const body = JSON.parse(response.body)

              if (body.accessToken) {
                body.accessToken = '[REDACTED]'
              }

              if (body.refreshToken) {
                body.refreshToken = '[REDACTED]'
              }

              if (body.token) {
                body.token = '[REDACTED]'
              }

              response.body = JSON.stringify(body)
            } catch {}
          }

          return response
        },
      },
      dom: {
        inputSanitizer: true,
        privateAttributeBlocklist: ['data-private'],
      },
    })

    setupLogRocketReact()
    LogRocket.getSessionURL((url) => {
      this.sessionURL = url
      this.sessionURLCallbacks.forEach((cb) => cb(url))
      this.sessionURLCallbacks = []
    })

    this.logrocketInitialized = true
    this.initialized = true
  }

  identify(user: LoggerUser): void {
    if (!this.initialized) {
      return
    }

    if (!this.logrocketInitialized) {
      return
    }

    let partnerId: string | undefined

    if (typeof user.partnerId === 'string') {
      partnerId = user.partnerId
    } else if (typeof (user as Record<string, unknown>).partner_id === 'string') {
      partnerId = (user as Record<string, string>).partner_id
    } else {
      partnerId = undefined
    }

    if (partnerId && LOGROCKET_BLOCKED_PARTNER_IDS.includes(partnerId)) {
      if (typeof window !== 'undefined') {
        void ((window as Window & { __lrBlockSession?: boolean }).__lrBlockSession = true)
      }

      return
    }

    const { id, ...traits } = user
    const cleanTraits: Record<string, string | number | boolean> = {}

    for (const [key, value] of Object.entries(traits)) {
      if (value !== undefined) {
        cleanTraits[key] = value
      }
    }

    LogRocket.identify(id, cleanTraits)
  }

  error(message: string, error?: Error, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    const errorObj = error !== undefined ? error : new Error(message)

    if (this.logrocketInitialized) {
      LogRocket.captureException(errorObj, {
        extra: toLogRocketExtra(data),
      })
    }

    Sentry.captureException(errorObj, (scope) => {
      scope.addBreadcrumb({ category: 'error', message, level: 'error' })

      if (data) {
        scope.setContext('data', data)
      }

      if (this.sessionURL) {
        scope.setTag('logrocket_session', this.sessionURL)
      }

      return scope
    })
  }

  warn(message: string, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    if (this.logrocketInitialized) {
      LogRocket.log(message, data)
    }

    Sentry.addBreadcrumb({ category: 'warning', message, level: 'warning', data })
  }

  info(message: string, data?: Record<string, unknown>): void {
    if (!this.initialized) {
      return
    }

    if (this.logrocketInitialized) {
      LogRocket.log(message, data)
    }

    Sentry.addBreadcrumb({ category: 'info', message, level: 'info', data })
  }

  track(event: string, properties?: LoggerTrackProperties): void {
    if (!this.initialized) {
      return
    }

    if (this.logrocketInitialized) {
      LogRocket.track(event, properties)
    }

    Sentry.addBreadcrumb({ category: 'track', message: event, level: 'info', data: properties })
  }

  getSessionURL(): string | null {
    return this.sessionURL
  }

  onSessionURLReady(callback: (url: string) => void): void {
    if (this.sessionURL) {
      callback(this.sessionURL)
    } else {
      this.sessionURLCallbacks.push(callback)
    }
  }

  createReduxMiddleware(): Middleware {
    if (
      !process.env.REACT_APP_LOGROCKET_APP_ID ||
      isSandbox() ||
      process.env.REACT_APP_LOGROCKET_RECORDING_DISABLED === 'true'
    ) {
      return () => (next) => (action) => next(action)
    }

    return LogRocket.reduxMiddleware({
      stateSanitizer: (state) => {
        if (!state) {
          return state
        }

        return sanitizeObject(state)
      },
      actionSanitizer: (action) => {
        if (!action) {
          return action
        }

        return sanitizeObject(action)
      },
    })
  }
}
